import type { ClubMemberRole, OwnershipSharePeriod } from '../../api';
import type { Aircraft, User } from '../../types';
import { aircraftLabel } from '../../aircraft/AircraftContext';
import { DateFormat } from '../../utils/DateFormat';
import {
  formatOwnershipShare,
  isPositiveOwnershipShare,
} from '../../utils/ownershipShare';
import { canHaveOwnershipShare } from '../../utils/isGeneralUser';
import ModalOverlay from '../modal/ModalOverlay';
import ModalPortal from '../modal/ModalPortal';
import { roleLabel, todayIso } from './membersUtils';

type Props = {
  members: User[];
  aircraftList: Aircraft[];
  sharesAircraftId: number;
  shareDrafts: Record<number, string>;
  shareDraftsByAircraft: Record<number, Record<number, string>>;
  sharesEffectiveFrom: string;
  sharesSaving: boolean;
  sharesLeaveUserId: number | null;
  sharesRoleChange: { userId: number; toRole: ClubMemberRole } | null;
  sharesOwnerIsPilot: boolean | null;
  lockAircraft?: boolean;
  modalShareSumLabel: string;
  modalShareSumOk: boolean;
  shareHistory: OwnershipSharePeriod[];
  historyLoading?: boolean;
  onClose: () => void;
  onSave: () => void;
  onEffectiveFromChange: (value: string) => void;
  onShareDraftChange: (userId: number, value: string) => void;
  onAircraftChange: (nextId: number, stored: Record<number, string> | undefined) => void;
  onStashCurrentDrafts: () => void;
  onDeletePeriod: (effectiveFrom: string) => void;
};

export default function OwnershipSharesModal({
  members,
  aircraftList,
  sharesAircraftId,
  shareDrafts,
  shareDraftsByAircraft,
  sharesEffectiveFrom,
  sharesSaving,
  sharesLeaveUserId,
  sharesRoleChange,
  sharesOwnerIsPilot,
  lockAircraft = false,
  modalShareSumLabel,
  modalShareSumOk,
  shareHistory,
  historyLoading = false,
  onClose,
  onSave,
  onEffectiveFromChange,
  onShareDraftChange,
  onAircraftChange,
  onStashCurrentDrafts,
  onDeletePeriod,
}: Props) {
  return (
    <ModalPortal>
      <ModalOverlay onClose={onClose}>
        <div
          className="modal members-shares-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="members-shares-modal-title"
        >
          <h3 id="members-shares-modal-title">
            {sharesOwnerIsPilot === true
              ? 'Назначить доли владельцу-собственнику'
              : sharesOwnerIsPilot === false
                ? 'Распределить долю при смене статуса'
                : sharesRoleChange != null
                  ? canHaveOwnershipShare(sharesRoleChange.toRole)
                    ? 'Назначить доли новому собственнику'
                    : 'Распределить долю при смене роли'
                  : sharesLeaveUserId != null
                    ? 'Распределить долю уходящего'
                    : members.some((m) => m.role === 'inactive')
                      ? 'Восстановить и назначить доли'
                      : 'Доли владения'}
          </h3>
          <p className="members-shares-modal-hint">
            {sharesOwnerIsPilot === true
              ? 'Вы становитесь собственником — назначьте доли. Сумма должна быть 1. Отмена — статус не изменится.'
              : sharesOwnerIsPilot === false
                ? 'Вы уходите из собственников — распределите свою долю. Сумма должна быть 1. Отмена — статус не изменится.'
                : sharesRoleChange != null
                  ? canHaveOwnershipShare(sharesRoleChange.toRole)
                    ? 'Участник станет собственником — назначьте доли. Сумма должна быть 1. Отмена — роль не изменится.'
                    : 'Участник уходит из собственников — распределите его долю. Сумма должна быть 1. Отмена — роль не изменится.'
                  : sharesLeaveUserId != null
                    ? 'Доля уходящего распределена поровну — можно изменить (например, отдать 1–2 участникам). Сумма должна быть 1. Отмена — удаление не выполнится.'
                    : members.some((m) => m.role === 'inactive')
                      ? 'Назначьте доли с учётом возвращающегося участника. Сумма должна быть 1. Отмена — восстановление не выполнится.'
                      : 'Укажите доли дробью (например 1/5). Сумма должна быть равна 1. Расходы делятся по долям, действовавшим на дату расхода.'}
            {members.some((m) => m.role === 'pending')
              ? ' После сохранения участник будет активирован.'
              : ''}
          </p>
          <label className="members-shares-effective">
            <span>Доли действуют с</span>
            <input
              type="date"
              value={sharesEffectiveFrom}
              max={todayIso()}
              disabled={sharesSaving}
              onChange={(e) => onEffectiveFromChange(e.target.value)}
            />
          </label>
          {aircraftList.length > 1 && !lockAircraft ? (
            <label className="members-aircraft-select">
              <span>Борт</span>
              <select
                value={sharesAircraftId || ''}
                disabled={sharesSaving}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  if (!Number.isFinite(next) || next <= 0) return;
                  onStashCurrentDrafts();
                  onAircraftChange(next, shareDraftsByAircraft[next]);
                }}
              >
                {aircraftList.map((ac) => (
                  <option key={ac.id} value={ac.id}>
                    {aircraftLabel(ac)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <div className="members-shares-list">
            {members.map((m) => (
              <label key={m.id} className="members-shares-row">
                <span className="members-shares-name">
                  {m.name}
                  <span className="members-shares-role">
                    {roleLabel(m.role, m.pending_role)}
                  </span>
                </span>
                <input
                  className="members-share-input"
                  type="text"
                  inputMode="text"
                  placeholder="1/5"
                  value={shareDrafts[m.id] ?? ''}
                  disabled={sharesSaving}
                  aria-label={`Доля владения ${m.name}`}
                  onChange={(e) => onShareDraftChange(m.id, e.target.value)}
                />
              </label>
            ))}
          </div>
            {!modalShareSumOk ? (
              <p className="members-share-sum members-shares-modal-sum members-share-sum-warn">
                Сумма: {modalShareSumLabel} — должна быть равна 1
              </p>
            ) : null}
          <div className="members-share-history">
            <h4 className="members-share-history-title">История долей</h4>
            {historyLoading ? (
              <p className="members-share-history-empty">Загрузка…</p>
            ) : shareHistory.length === 0 ? (
              <p className="members-share-history-empty">Снимков пока нет</p>
            ) : (
              <ul className="members-share-history-list">
                {shareHistory.map((period) => {
                  const range = period.effective_until
                    ? `${DateFormat(period.effective_from)} — ${DateFormat(
                        period.effective_until
                      )}`
                    : `с ${DateFormat(period.effective_from)}`;
                  const people = period.shares
                    .filter((row) =>
                      isPositiveOwnershipShare(row.ownership_share)
                    )
                    .map(
                      (row) =>
                        `${row.name} ${formatOwnershipShare(row.ownership_share)}`
                    )
                    .join(' · ');
                  return (
                    <li key={period.effective_from}>
                      <div className="members-share-history-item">
                        <span className="members-share-history-range">
                          {range}
                        </span>
                        <span className="members-share-history-people">
                          {people || '—'}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="members-share-history-delete"
                        disabled={sharesSaving}
                        onClick={() => onDeletePeriod(period.effective_from)}
                      >
                        Удалить
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-save"
              disabled={sharesSaving}
              onClick={() => {
                void onSave();
              }}
            >
              {sharesSaving
                ? 'Сохранение…'
                : sharesOwnerIsPilot != null
                  ? 'Сохранить статус и доли'
                  : sharesRoleChange != null
                    ? 'Сохранить роль и доли'
                    : sharesLeaveUserId != null
                      ? 'Удалить и сохранить'
                      : members.some((m) => m.role === 'inactive')
                        ? 'Восстановить и сохранить'
                        : 'Сохранить'}
            </button>
            <button
              type="button"
              className="btn-cancel"
              disabled={sharesSaving}
              onClick={onClose}
            >
              Отмена
            </button>
          </div>
        </div>
      </ModalOverlay>
    </ModalPortal>
  );
}
